import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, 
  MapPin,
  Clock,
  ArrowRight,
  Send
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Mock submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success('Message sent successfully!', {
      description: 'We will get back to you within 24 hours.'
    });
    
    setFormData({
      name: '',
      email: '',
      message: ''
    });
    setIsSubmitting(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      content: 'info@fleethire.ca',
      link: 'mailto:info@fleethire.ca',
      description: 'We respond within 24 hours'
    },
    {
      icon: MapPin,
      title: 'Office',
      content: '30 Kennedy Rd S',
      link: null,
      description: 'Brampton, ON L6W 3E2'
    },
    {
      icon: Clock,
      title: 'Hours',
      content: 'Monday - Friday',
      link: null,
      description: '8:00 AM - 6:00 PM EST'
    }
  ];

  return (
    <main data-testid="contact-page">
      {/* Hero Section */}
      <section className="page-header" data-testid="contact-hero">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-2 bg-white/10 rounded-full text-white/90 text-sm font-medium mb-6">
              Get in Touch
            </span>
            <h1 className="font-['Chivo'] font-black text-4xl md:text-5xl lg:text-6xl text-white leading-tight tracking-tight mb-6">
              Contact Us
            </h1>
            <p className="text-lg md:text-xl text-white/80 leading-relaxed">
              Have questions? Need staffing solutions? We're here to help. Reach out and let's discuss how we can support your business.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white border-b border-[#E2E8F0]" data-testid="contact-info-cards">
        <div className="container-custom">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="border border-[#E2E8F0] p-6 hover:border-[#1E90FF] transition-colors"
              >
                <div className="w-12 h-12 bg-[#F5F7FA] rounded-lg flex items-center justify-center mb-4">
                  <info.icon size={24} className="text-[#1E90FF]" strokeWidth={1.5} />
                </div>
                <h3 className="font-['Chivo'] font-bold text-lg text-[#0B1F3A] mb-1">
                  {info.title}
                </h3>
                {info.link ? (
                  <a 
                    href={info.link}
                    className="text-[#1E90FF] hover:underline block mb-1"
                  >
                    {info.content}
                  </a>
                ) : (
                  <p className="text-[#0B1F3A] mb-1">{info.content}</p>
                )}
                <p className="text-sm text-[#64748B]">{info.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="section-padding bg-[#F5F7FA]" data-testid="contact-form-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className="text-sm text-[#64748B] uppercase tracking-[0.2em] font-bold">
                Send a Message
              </span>
              <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-[#0B1F3A] mt-3 tracking-tight mb-6">
                We'd Love to Hear From You
              </h2>
              <p className="text-[#64748B] mb-8">
                Fill out the form below and our team will get back to you within 24 hours.
              </p>

              <form onSubmit={handleSubmit} className="bg-white p-8 border border-[#E2E8F0]" data-testid="contact-form">
                <div className="mb-4">
                  <label htmlFor="name" className="block text-sm font-medium text-[#0B1F3A] mb-2">
                    Full Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                    className="w-full"
                    data-testid="contact-input-name"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="email" className="block text-sm font-medium text-[#0B1F3A] mb-2">
                    Email Address *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                    className="w-full"
                    data-testid="contact-input-email"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-[#0B1F3A] mb-2">
                    Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="How can we help you?"
                    rows={5}
                    className="w-full"
                    data-testid="contact-input-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full justify-center text-lg py-4 disabled:opacity-50"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  {!isSubmitting && <Send size={18} strokeWidth={2} />}
                </button>
              </form>
            </motion.div>

            {/* Map Placeholder */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className="text-sm text-[#64748B] uppercase tracking-[0.2em] font-bold">
                Our Location
              </span>
              <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-[#0B1F3A] mt-3 tracking-tight mb-6">
                Visit Our Office
              </h2>
              <p className="text-[#64748B] mb-8">
                Located in the heart of Mississauga's business district, our office is easily accessible from Highway 401 and 403.
              </p>

              {/* Map Placeholder */}
              <div className="map-placeholder aspect-[4/3] rounded-lg" data-testid="map-placeholder">
                <MapPin size={48} className="mb-4" strokeWidth={1} />
                <p className="font-['Chivo'] font-bold text-lg text-[#0B1F3A]">
                  30 Kennedy Rd S
                </p>
                <p className="text-[#64748B]">
                  Brampton, ON L6W 3E2
                </p>
                <a 
                  href="https://maps.google.com/?q=30+Kennedy+Rd+S+Brampton+ON+L6W+3E2" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 text-[#1E90FF] hover:underline"
                >
                  Get Directions
                  <ArrowRight size={14} strokeWidth={2} />
                </a>
              </div>

              {/* Quick Contact */}
              <div className="mt-8 bg-[#0B1F3A] p-6 rounded-lg">
                <h3 className="font-['Chivo'] font-bold text-lg text-white mb-4">
                  Need Immediate Assistance?
                </h3>
                <p className="text-[#94A3B8] mb-4">
                  Send us an email and we'll respond within 24 hours.
                </p>
                <a 
                  href="mailto:info@fleethire.ca"
                  className="btn-primary w-full justify-center"
                  data-testid="quick-email-btn"
                >
                  <Mail size={18} strokeWidth={2} />
                  Email Us
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-white" data-testid="faq-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-[#0B1F3A] tracking-tight text-center mb-12">
              Frequently Asked Questions
            </h2>

            <div className="space-y-6">
              <div className="border border-[#E2E8F0] p-6">
                <h3 className="font-['Chivo'] font-bold text-lg text-[#0B1F3A] mb-2">
                  How quickly can you provide workers?
                </h3>
                <p className="text-[#64748B]">
                  For most positions, we can deploy qualified workers within 24-48 hours. For urgent needs, same-day placement may be available depending on the role and requirements.
                </p>
              </div>

              <div className="border border-[#E2E8F0] p-6">
                <h3 className="font-['Chivo'] font-bold text-lg text-[#0B1F3A] mb-2">
                  What industries do you serve?
                </h3>
                <p className="text-[#64748B]">
                  We specialize in warehousing, manufacturing, transportation, and administrative staffing across the Greater Toronto Area.
                </p>
              </div>

              <div className="border border-[#E2E8F0] p-6">
                <h3 className="font-['Chivo'] font-bold text-lg text-[#0B1F3A] mb-2">
                  Are your workers pre-screened?
                </h3>
                <p className="text-[#64748B]">
                  Yes, all candidates go through our comprehensive screening process including background checks, reference verification, and skills assessment before placement.
                </p>
              </div>

              <div className="border border-[#E2E8F0] p-6">
                <h3 className="font-['Chivo'] font-bold text-lg text-[#0B1F3A] mb-2">
                  Do you offer temp-to-perm options?
                </h3>
                <p className="text-[#64748B]">
                  Absolutely. Many of our clients start with temporary placements and transition workers to permanent roles after evaluating their performance on the job.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
